import ApolloErrorBoundary from '@components/ApolloErrorBoundary';
import { TextFieldController } from '@components/controllers/TextFieldController';
import { StepProps } from '@features/reports2/types';
import { msg, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { Add, Delete, GridOn, PictureAsPdf } from '@mui/icons-material';
import {
  Box,
  Button,
  ButtonBase,
  Checkbox,
  FormHelperText,
  IconButton,
  Typography,
} from '@mui/material';
import { FC, Suspense } from 'react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { CreateReportFieldValues } from '../../validation';
import { InnerPaper, StepContentBox, StepTitle } from '../common';

const SuspenseComponent: FC<StepProps> = (props) => {
  const methods = useFormContext<CreateReportFieldValues>();
  const { _ } = useLingui();

  const errors = methods.formState.errors.thirdStep;
  const nextStepEnabled = !errors?.createPdf && !errors?.createExcel && !errors?.emails;

  const { fields, append, remove } = useFieldArray({
    control: methods.control,
    name: 'thirdStep.emails',
  });

  return (
    <StepContentBox
      actions={{
        ...props.actions,
        nextStepEnabled: props.actions.nextStepEnabled && nextStepEnabled,
        onNextStep: () => {
          methods.trigger('thirdStep').then((success) => {
            if (success) {
              props.actions.onNextStep();
            }
          });
        },
      }}
    >
      <StepTitle
        title={<Trans>Formato y destinatarios</Trans>}
        description={<Trans>Elige el formato de envío y los destinatarios que desees.</Trans>}
      />
      <InnerPaper>
        <Typography fontWeight="bold" mb={2}>
          <Trans>Formatos de reporte</Trans>
        </Typography>
        <Box display="flex" gap={2}>
          {(
            [
              ['createPdf', <Trans>PDF</Trans>, PictureAsPdf],
              ['createExcel', <Trans>Excel</Trans>, GridOn],
            ] as const
          ).map(([value, label, Icon]) => (
            <Controller
              key={value}
              control={methods.control}
              name={`thirdStep.${value}`}
              render={({ field }) => (
                <ButtonBase
                  key={value}
                  onClick={() => {
                    field.onChange(!field.value);
                    methods.trigger('thirdStep');
                  }}
                  sx={{
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    width: 180,
                    height: 60,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 2,
                    gap: 2,
                  }}
                >
                  <Box display="flex" gap={2} alignItems="center">
                    <Icon />
                    <Typography fontWeight="medium">{label}</Typography>
                  </Box>
                  <Checkbox disableRipple checked={field.value} />
                </ButtonBase>
              )}
            />
          ))}
        </Box>
        {(errors?.createPdf || errors?.createExcel) && (
          <FormHelperText sx={{ mt: 1 }} error>
            <Trans>Debes seleccionar al menos un formato</Trans>
          </FormHelperText>
        )}
      </InnerPaper>
      <InnerPaper mt={4}>
        <Typography fontWeight="bold" mb={2}>
          <Trans>Correos de destino del reporte</Trans>
        </Typography>
        <Box display="flex" flexDirection="column" alignItems="start" width="100%" gap={1}>
          {fields.length === 0 && (
            <FormHelperText sx={{ mt: 1 }} error>
              <Trans>Debes indicar al menos 1 correo</Trans>
            </FormHelperText>
          )}
          {fields.map((_field, index) => (
            <Box display="flex" gap={2} width="100%" alignItems="center">
              <TextFieldController
                control={methods.control}
                label={_(msg`Correo electrónico`)}
                name={`thirdStep.emails.${index}.email`}
                slotProps={{
                  textField: {
                    size: 'small',
                    fullWidth: true,
                    sx: {
                      maxWidth: 400,
                    },
                  },
                }}
              />
              <IconButton onClick={() => remove(index)} disabled={fields.length === 1}>
                <Delete />
              </IconButton>
            </Box>
          ))}
          <Button
            sx={{ mt: 2 }}
            startIcon={<Add />}
            onClick={() => {
              append({
                email: '',
              });
            }}
          >
            <Trans>Agregar otro correo</Trans>
          </Button>
        </Box>
      </InnerPaper>
    </StepContentBox>
  );
};

export const FormatAndRecipientsStep: FC<StepProps> = (props) => {
  return (
    <ApolloErrorBoundary>
      <Suspense>
        <SuspenseComponent {...props} />
      </Suspense>
    </ApolloErrorBoundary>
  );
};
